import type { AdminBreakdown } from '../../lib/apiClient';

/**
 * The console's bar charts (spec §6).
 *
 * Production renders three: Bottleneck Types and Work Fit Profiles as vertical
 * bars, Bottleneck Commonalities as horizontal bars. Each carries a title, a
 * caption, and a four-interval axis.
 *
 * **The axis is reproduced from what was observed.** Bottleneck Types and
 * Commonalities were seen at `0 / 15 / 30 / 45 / 60`, Work Fit Profiles at
 * `0 / 3 / 6 / 9 / 12` — four intervals in both cases, with the step rounded up
 * to a round number. `axisTicks` is that rule, and it reproduces both observed
 * axes from their data.
 */
const NICE_STEPS = [1, 2, 3, 5, 10, 15, 20, 25, 50, 100, 200, 250, 500, 1000];

/**
 * Production's `CHART_COLORS` — eight shades of amber, darkening down the list
 * and cycling if a chart has more bars than that.
 *
 * **Hex, not utilities, and that is deliberate.** These are data colours, not
 * theme colours: they must stay in the same order for the same chart in both
 * themes, because the ninth bar being the same shade as the first is how the
 * cycle reads. Tailwind's palette would also put them under the visual system's
 * prohibited-utility rule (section B.2). Production sets them inline too.
 */
export const CHART_COLORS = [
  '#f5b800',
  '#e8a000',
  '#d99000',
  '#c98000',
  '#b87000',
  '#a76000',
  '#965000',
  '#854000',
];

export const barColor = (index: number): string =>
  CHART_COLORS[index % CHART_COLORS.length] as string;

/** Five ticks, four intervals, a round step — production's observed axes. */
export function axisTicks(max: number): number[] {
  const raw = Math.max(max, 0) / 4;
  const step = NICE_STEPS.find((candidate) => candidate >= raw) ?? Math.ceil(raw);
  return [0, 1, 2, 3, 4].map((i) => i * step);
}

/**
 * Production's chart labels are the performer type without its article and with
 * "High Performer" abbreviated: `The Overextended High Performer` renders as
 * `Overextended HP`. The rule reproduces every label observed on both type
 * charts, and leaves the others — `Isolated Operator`, `Scattered Builder` —
 * untouched, which is also what production shows.
 */
export function chartLabel(label: string): string {
  return label.replace(/^The /, '').replace(/High Performer$/, 'HP');
}

interface ChartProps {
  title: string;
  caption: string;
  data: AdminBreakdown[];
  testId: string;
  /** Applied to the bar labels. The commonality labels are already short. */
  shorten?: boolean;
}

/**
 * Production's empty chart: the frame keeps its height and says this, centred
 * and muted. Both bar charts share the sentence.
 */
export const NO_CHART_DATA = 'No assessment data yet.';

function EmptyChart() {
  return (
    <p
      className="flex h-48 items-center justify-center text-sm text-muted-foreground"
      data-testid="chart-empty"
    >
      {NO_CHART_DATA}
    </p>
  );
}

export function VerticalBarChart({ title, caption, data, testId, shorten }: ChartProps) {
  const ticks = axisTicks(Math.max(0, ...data.map((d) => d.count)));
  const top = ticks[ticks.length - 1] ?? 1;

  if (data.length === 0) {
    return (
      <ChartFrame title={title} caption={caption} testId={testId}>
        <EmptyChart />
      </ChartFrame>
    );
  }

  return (
    <ChartFrame title={title} caption={caption} testId={testId}>
      <div className="flex gap-3">
        {/* The axis, highest tick first, so the column reads top to bottom. */}
        <div className="flex h-48 flex-col justify-between text-xs tabular-nums text-muted-foreground">
          {[...ticks].reverse().map((tick) => (
            <span key={tick}>{tick}</span>
          ))}
        </div>

        <div className="flex flex-1 items-end gap-2 border-l border-b border-border pl-3">
          {data.map((bar, index) => (
            <div
              key={bar.label}
              className="flex h-48 flex-1 flex-col items-center justify-end gap-1"
              data-testid="chart-bar"
              data-label={bar.label}
              data-count={bar.count}
              /* Production's tooltip text, as a native one. */
              title={`${bar.label}: ${bar.count} assessments`}
            >
              <span className="text-xs tabular-nums text-muted-foreground">{bar.count}</span>
              <div
                className="w-full rounded-t"
                style={{
                  height: `${percentage(bar.count, top)}%`,
                  backgroundColor: barColor(index),
                }}
              />
              <span className="text-center text-xs leading-tight text-muted-foreground">
                {shorten ? chartLabel(bar.label) : bar.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </ChartFrame>
  );
}

export function HorizontalBarChart({ title, caption, data, testId }: ChartProps) {
  const ticks = axisTicks(Math.max(0, ...data.map((d) => d.count)));
  const top = ticks[ticks.length - 1] ?? 1;

  if (data.length === 0) {
    return (
      <ChartFrame title={title} caption={caption} testId={testId}>
        <EmptyChart />
      </ChartFrame>
    );
  }

  return (
    <ChartFrame title={title} caption={caption} testId={testId}>
      <div className="flex flex-col gap-2">
        {data.map((bar, index) => (
          <div
            key={bar.label}
            className="flex items-center gap-3"
            data-testid="chart-bar"
            data-label={bar.label}
            data-count={bar.count}
            title={`${bar.label}: ${bar.count} assessments`}
          >
            <span className="w-32 shrink-0 text-right text-xs text-muted-foreground">
              {bar.label}
            </span>
            <div className="flex flex-1 items-center gap-2 border-l border-border pl-2">
              <div
                className="h-5 rounded-r"
                style={{
                  width: `${percentage(bar.count, top)}%`,
                  backgroundColor: barColor(index),
                }}
              />
              <span className="text-xs tabular-nums text-muted-foreground">{bar.count}</span>
            </div>
          </div>
        ))}

        <div className="flex gap-3">
          <span className="w-32 shrink-0" />
          <div className="flex flex-1 justify-between pl-2 text-xs tabular-nums text-muted-foreground">
            {ticks.map((tick) => (
              <span key={tick}>{tick}</span>
            ))}
          </div>
        </div>
      </div>
    </ChartFrame>
  );
}

function ChartFrame({
  title,
  caption,
  testId,
  children,
}: {
  title: string;
  caption: string;
  testId: string;
  children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-4" data-testid={testId}>
      <div className="flex flex-col gap-1">
        <h2 className="text-xl font-bold">{title}</h2>
        <p className="text-sm text-muted-foreground">{caption}</p>
      </div>
      <div className="rounded-xl border border-border bg-card p-5">{children}</div>
    </section>
  );
}

const percentage = (count: number, top: number): number => (top > 0 ? (count / top) * 100 : 0);
